/**
 * Thread Manager
 *
 * Manages agent conversation threads with token tracking.
 *
 * Responsibilities:
 * - Create/access thread histories
 * - Track token usage per thread (percentage, actual, max)
 * - Decide when to continue vs start new thread (60-66% threshold)
 * - Store thread messages
 */
import { generateThreadId } from './idGenerator';
import { getContextWindow } from '@carousel-office/shared';
// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════
/** Token thresholds for thread management */
const TOKEN_THRESHOLDS = {
    /** Below this: plenty of room, continue */
    LOW: 50,
    /** Above this: moderate usage, continue with caution */
    MODERATE: 60,
    /** Above this: start new thread recommended */
    HIGH: 66,
};
/** Fallback context window sizes by model family.
 *  Prefer using getContextWindow(modelId) from shared/constants/models
 *  for exact per-model values. This table is only used when modelId is not set.
 */
const DEFAULT_CONTEXT_WINDOWS = {
    'claude': 200000, // conservative default; actual model context set via modelId
    'gemini': 1048576, // ~1M native
    'gpt-4': 128000,
    'gpt-3.5': 16000,
    'default': 200000,
};
// ═══════════════════════════════════════════════════════════════════════════
// THREAD MANAGER CLASS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Manager for agent conversation threads
 *
 * @example
 * // Create or get a thread
 * const threadId = threadManager.getOrCreateThread({
 *     agentType: 'alphasense',
 *     conversationId: 'conv_abc123',
 *     documentId: 'deck_xyz789',
 * });
 *
 * // Add messages
 * threadManager.addMessage(threadId, { role: 'user', content: 'Search for...' });
 *
 * // Check if should continue
 * const decision = threadManager.shouldContinueThread(threadId);
 * if (!decision.continue) {
 *     // Start new thread
 * }
 */
export class ThreadManager {
    /**
     * Constructor - creates a new ThreadManager instance
     * Typically owned by an AgentRegistry for per-conversation thread isolation
     */
    constructor() {
        /** All threads by ID */
        this.threads = new Map();
        /** Model being used (affects context window size) */
        this.modelFamily = 'default';
        /** Full model ID (e.g., "anthropic/claude-opus-4-6") — set via setModelId() */
        this.modelId = null;
        /** Currently active thread ID */
        this.activeThreadId = null;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // CONFIGURATION
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Set the full model ID for accurate context window sizing.
     * Call this when the model is known (e.g., from settings).
     */
    setModelId(modelId) {
        this.modelId = modelId;
    }
    /**
     * Get the context window size for the current model.
     * Uses the exact model ID if set, falls back to family-based lookup.
     */
    getContextWindowSize() {
        if (this.modelId) {
            return getContextWindow(this.modelId);
        }
        return DEFAULT_CONTEXT_WINDOWS[this.modelFamily] || DEFAULT_CONTEXT_WINDOWS.default;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // ACTIVE THREAD MANAGEMENT
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Set the active thread
     *
     * @param threadId - Thread to make active, or null to clear
     */
    setActiveThread(threadId) {
        if (threadId && !this.threads.has(threadId)) {
            console.warn(`[ThreadManager] Cannot set active thread - ${threadId} not found`);
            return;
        }
        this.activeThreadId = threadId;
        console.log('[ThreadManager] Active thread set:', threadId ?? 'none');
    }
    /**
     * Get the active thread ID
     */
    getActiveThreadId() {
        return this.activeThreadId;
    }
    // REMOVED: getActiveThread, getActiveMessages, addMessageToActive, hasActiveThread
    // - Convenience wrappers never used; agents explicitly pass threadId
    // ═══════════════════════════════════════════════════════════════════════
    // THREAD LIFECYCLE
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Create a new thread
     *
     * @param params - Thread configuration
     * @param setActive - Whether to set this as the active thread (default: true)
     */
    createThread(params, setActive = true) {
        const threadId = generateThreadId(params.agentType);
        const thread = {
            threadId,
            agentType: params.agentType,
            conversationId: params.conversationId,
            documentId: params.documentId,
            documentType: params.documentType || 'unknown',
            messages: [],
            createdAt: Date.now(),
            lastActivityAt: Date.now(),
            tokenUsage: {
                used: 0,
                max: this.getContextWindowSize(),
            },
        };
        this.threads.set(threadId, thread);
        if (setActive) {
            this.activeThreadId = threadId;
        }
        console.log(`[ThreadManager] Created thread ${threadId}${setActive ? ' (active)' : ''}`);
        return threadId;
    }
    /**
     * Restore a thread from persisted state
     *
     * Used when loading conversations from disk - preserves the original thread ID
     * instead of generating a new one.
     *
     * @param threadState - The full thread state to restore
     * @param setActive - Whether to set this as the active thread (default: false)
     */
    restoreThread(threadState, setActive = false) {
        // Validate the thread state has required fields
        if (!threadState.threadId || !threadState.agentType || !threadState.conversationId) {
            console.warn('[ThreadManager] Invalid thread state for restoration:', threadState);
            return;
        }
        // Check if thread already exists (avoid duplicates)
        if (this.threads.has(threadState.threadId)) {
            console.log(`[ThreadManager] Thread ${threadState.threadId} already exists, updating`);
        }
        // Store the thread with its original ID
        this.threads.set(threadState.threadId, {
            ...threadState,
            // Ensure max tokens is set if not provided
            tokenUsage: {
                used: threadState.tokenUsage?.used ?? 0,
                max: threadState.tokenUsage?.max ?? this.getContextWindowSize(),
            },
        });
        if (setActive) {
            this.activeThreadId = threadState.threadId;
        }
        console.log(`[ThreadManager] Restored thread ${threadState.threadId} with ${threadState.messages?.length ?? 0} messages${setActive ? ' (active)' : ''}`);
    }
    /**
     * Get an existing thread or create a new one
     *
     * Thread lookup priority:
     * 1. If existingThreadId is provided and valid, use it
     * 2. Otherwise, find most recent thread for this agentType + conversationId
     * 3. If no suitable thread found, create a new one
     */
    getOrCreateThread(params) {
        console.log(`[ThreadManager] 🔍 getOrCreateThread called:`, {
            agentType: params.agentType,
            conversationId: params.conversationId,
            existingThreadId: params.existingThreadId,
            totalThreadsInMemory: this.threads.size,
        });
        // If existing thread provided and it exists, use it
        if (params.existingThreadId && this.threads.has(params.existingThreadId)) {
            const thread = this.threads.get(params.existingThreadId);
            // Check if we should continue this thread
            const decision = this.shouldContinueThread(params.existingThreadId);
            if (decision.continue) {
                console.log(`[ThreadManager] ✅ Continuing explicit thread ${params.existingThreadId} (${thread.messages.length} messages)`);
                this.setActiveThread(params.existingThreadId);
                return params.existingThreadId;
            }
            else {
                console.log(`[ThreadManager] ⚠️ Thread ${params.existingThreadId} at ${decision.usagePercent}%, creating new thread`);
            }
        }
        // Try to find an existing thread for this agent type and conversation
        // This is the KEY FIX: reuse threads by agentType+conversationId even if threadId wasn't passed
        const existingThread = this.getMostRecentThread(params.agentType, params.conversationId);
        if (existingThread) {
            const decision = this.shouldContinueThread(existingThread.threadId);
            if (decision.continue) {
                console.log(`[ThreadManager] ✅ REUSING existing thread for ${params.agentType}/${params.conversationId}:`, {
                    threadId: existingThread.threadId,
                    messageCount: existingThread.messages.length,
                    usagePercent: decision.usagePercent.toFixed(1) + '%',
                });
                this.setActiveThread(existingThread.threadId);
                return existingThread.threadId;
            }
            else {
                console.log(`[ThreadManager] ⚠️ Existing thread at ${decision.usagePercent.toFixed(1)}%, creating new thread`);
            }
        }
        else {
            console.log(`[ThreadManager] 🆕 No existing thread found for ${params.agentType}/${params.conversationId}, creating new one`);
        }
        // Create new thread
        return this.createThread({
            agentType: params.agentType,
            conversationId: params.conversationId,
            documentId: params.documentId,
            documentType: params.documentType,
        });
    }
    /**
     * Get a thread by ID
     */
    getThread(threadId) {
        return this.threads.get(threadId);
    }
    /**
     * Check if a thread exists
     */
    hasThread(threadId) {
        return this.threads.has(threadId);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // MESSAGE MANAGEMENT
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Get messages for a thread
     */
    getMessages(threadId) {
        const thread = this.threads.get(threadId);
        return thread?.messages ?? [];
    }
    /**
     * Add a message to a thread
     */
    addMessage(threadId, message) {
        const thread = this.threads.get(threadId);
        if (!thread) {
            console.warn(`[ThreadManager] Thread ${threadId} not found`);
            return;
        }
        // Add timestamp if not already present (for latency tracking)
        message.timestamp = message.timestamp ?? Date.now();
        thread.messages.push(message);
        thread.lastActivityAt = Date.now();
        // Estimate token usage increase
        const messageTokens = this.estimateTokens(message);
        thread.tokenUsage.used += messageTokens;
    }
    /**
     * Add multiple messages to a thread
     */
    addMessages(threadId, messages) {
        for (const message of messages) {
            this.addMessage(threadId, message);
        }
    }
    /**
     * Clear messages from a thread (but keep the thread)
     */
    clearMessages(threadId) {
        const thread = this.threads.get(threadId);
        if (thread) {
            thread.messages = [];
            thread.tokenUsage.used = 0;
        }
    }
    // ═══════════════════════════════════════════════════════════════════════
    // TOKEN TRACKING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Estimate tokens for a message (rough approximation)
     * ~4 characters per token for English text
     */
    estimateTokens(message) {
        let content = '';
        if (typeof message.content === 'string') {
            content = message.content;
        }
        else if (Array.isArray(message.content)) {
            content = message.content
                .filter(part => part.type === 'text')
                .map(part => part.text)
                .join(' ');
        }
        // ~4 chars per token, plus overhead for role etc.
        return Math.ceil(content.length / 4) + 10;
    }
    /**
     * Update token usage with actual values (after LLM call)
     */
    updateTokenUsage(threadId, tokensUsed) {
        const thread = this.threads.get(threadId);
        if (thread) {
            thread.tokenUsage.used = tokensUsed;
            thread.lastActivityAt = Date.now();
        }
    }
    // REMOVED: setMaxTokens - never called externally (max comes from context window defaults)
    /**
     * Get token usage percentage for a thread
     */
    getUsagePercent(threadId) {
        const thread = this.threads.get(threadId);
        if (!thread)
            return 0;
        const { used, max } = thread.tokenUsage;
        return (used / max) * 100;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // THREAD DECISIONS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Decide whether to continue using a thread or start a new one
     */
    shouldContinueThread(threadId) {
        const thread = this.threads.get(threadId);
        if (!thread) {
            return {
                continue: false,
                reason: 'Thread not found',
                usagePercent: 0,
            };
        }
        const usagePercent = this.getUsagePercent(threadId);
        if (usagePercent < TOKEN_THRESHOLDS.LOW) {
            return {
                continue: true,
                reason: 'Plenty of context room available',
                usagePercent,
            };
        }
        if (usagePercent < TOKEN_THRESHOLDS.MODERATE) {
            return {
                continue: true,
                reason: 'Moderate usage, can continue',
                usagePercent,
            };
        }
        if (usagePercent < TOKEN_THRESHOLDS.HIGH) {
            return {
                continue: true,
                reason: 'Approaching limit, consider starting new thread soon',
                usagePercent,
            };
        }
        return {
            continue: false,
            reason: `Token usage at ${usagePercent.toFixed(1)}% - exceeds ${TOKEN_THRESHOLDS.HIGH}% threshold`,
            usagePercent,
        };
    }
    // ═══════════════════════════════════════════════════════════════════════
    // THREAD CONTEXT BUILDING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Build thread context for inclusion in agent results
     */
    buildThreadContext(threadId, actualTokensUsed, maxTokens) {
        const thread = this.threads.get(threadId);
        const contextWindowSize = this.getContextWindowSize();
        // Even if thread doesn't exist, use actualTokensUsed if provided
        if (!thread) {
            const max = maxTokens ?? contextWindowSize;
            const used = actualTokensUsed ?? 0;
            return {
                threadId,
                threadContextUsed: used > 0 ? (used / max) * 100 : 0,
                threadContextUsedTokens: used,
                threadContextMaxTokens: max,
            };
        }
        // Use actual values if provided, otherwise use thread's stored values
        const used = actualTokensUsed ?? thread.tokenUsage.used;
        const max = maxTokens ?? thread.tokenUsage.max;
        return {
            threadId,
            threadContextUsed: (used / max) * 100,
            threadContextUsedTokens: used,
            threadContextMaxTokens: max,
        };
    }
    // ═══════════════════════════════════════════════════════════════════════
    // QUERIES
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Get all threads for a conversation
     */
    getThreadsForConversation(conversationId) {
        return Array.from(this.threads.values()).filter(thread => thread.conversationId === conversationId);
    }
    /**
     * Get all threads for an agent type
     */
    getThreadsForAgentType(agentType) {
        return Array.from(this.threads.values()).filter(thread => thread.agentType === agentType);
    }
    /**
     * Get the most recent thread for an agent in a conversation
     */
    getMostRecentThread(agentType, conversationId) {
        const threads = Array.from(this.threads.values())
            .filter(t => t.agentType === agentType && t.conversationId === conversationId)
            .sort((a, b) => b.lastActivityAt - a.lastActivityAt);
        return threads[0];
    }
    // ═══════════════════════════════════════════════════════════════════════
    // CLEANUP
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Delete a thread
     */
    deleteThread(threadId) {
        this.threads.delete(threadId);
        // Clear active thread if it was deleted
        if (this.activeThreadId === threadId) {
            this.activeThreadId = null;
        }
        console.log('[ThreadManager] Deleted thread:', threadId);
    }
    /**
     * Delete all threads for a conversation
     */
    deleteThreadsForConversation(conversationId) {
        const toDelete = Array.from(this.threads.values())
            .filter(t => t.conversationId === conversationId)
            .map(t => t.threadId);
        for (const threadId of toDelete) {
            this.threads.delete(threadId);
            // Clear active thread if it was deleted
            if (this.activeThreadId === threadId) {
                this.activeThreadId = null;
            }
        }
        console.log('[ThreadManager] Deleted threads:', {
            count: toDelete.length,
            conversationId
        });
    }
    /**
     * Clear all threads (for testing)
     */
    clearAll() {
        this.threads.clear();
        this.activeThreadId = null;
    }
}
